import { useEffect, useState } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, Box, Skeleton, IconButton, Stack, MenuItem,
    Checkbox, FormControlLabel, Chip, OutlinedInput, Select,
    InputLabel, FormControl, FormHelperText, SelectChangeEvent, TextField,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import * as AuditWpObjectiveService from '@/services/auditService/auditWpObjectiveService';
import { UpdateObjectiveCodeBO } from '@/services/auditService/auditWpObjectiveService';
import { FsCategoryService } from '@/services/accountService'; // 新增导入

interface Props {
    open: boolean;
    objectiveId?: string;
    onClose: (refreshed?: boolean) => void;
    onSnackbar: (message: string, severity: 'success' | 'error') => void;
}

export default function WorkeDoneDetail({ open, objectiveId, onClose, onSnackbar }: Props) {
    const isEdit = !!objectiveId;
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [wpOptions, setWpOptions] = useState<Record<string, string>>({});

    const [form, setForm] = useState<{
        parentId: string;
        symbol: string;
        content: string;
        code: string[];
        isDel: number; // 0-未删除, 1-已删除
    }>({
        parentId: '0',
        symbol: '',
        content: '',
        code: [],
        isDel: 0,
    });

    const [fieldErrors, setFieldErrors] = useState<{
        parentId?: string;
        symbol?: string;
        content?: string;
        _global?: string;
    }>({});

    // 加载 Working Paper 选项（完全参照参考代码）
    const loadWpOptions = async () => {
        try {
            const res = await FsCategoryService.queryFsCategorySelection();
            setWpOptions(res || {});
        } catch (error) {
            console.error('Failed to load Working Paper options', error);
            setWpOptions({});
        }
    };

    // 加载详情（包含 is_del 字段）
    const loadDetail = async () => {
        if (!objectiveId) return;
        setLoading(true);
        try {
            const data = await AuditWpObjectiveService.getById(objectiveId);
            setForm({
                parentId: data.parentId ? data.parentId : '0',
                symbol: data.symbol || '',
                content: data.content || '',
                code: data.code || [],
                isDel: data.isDel ?? 0,
            });
        } catch (err: any) {
            onSnackbar(err?.message || 'Load failed', 'error');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (open) {
            loadWpOptions(); // 使用修改后的加载函数
            if (isEdit) {
                loadDetail();
            } else {
                setForm({
                    parentId: '0',
                    symbol: '',
                    content: '',
                    code: [],
                    isDel: 0,
                });
                setFieldErrors({});
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, objectiveId]);

    const validate = (): boolean => {
        const errors: typeof fieldErrors = {};
        const parentNum = parseInt(form.parentId, 10);
        if (isNaN(parentNum) || parentNum < 0 || parentNum > 4) {
            errors.parentId = 'Parent ID must be 0~4 (0 = Main table, 1~4 = Sub-table level)';
        }
        if (!form.content.trim()) errors.content = 'Content is required';
        setFieldErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSave = async () => {
        if (!validate()) return;
        setSaving(true);
        try {
            const bo: UpdateObjectiveCodeBO = {
                id: objectiveId,
                parentId: form.parentId === '0' ? null : form.parentId,
                symbol: form.symbol || undefined,
                content: form.content,
                code: form.code,
                isDel: form.isDel,
            };
            if (isEdit) {
                await AuditWpObjectiveService.updateCodeById(bo);
            } else {
                await AuditWpObjectiveService.add(bo);
            }
            onSnackbar('Saved successfully', 'success');
            onClose(true);
        } catch (e: any) {
            setFieldErrors({ _global: e?.message || 'Save failed' });
        } finally {
            setSaving(false);
        }
    };

    // Working Paper 多选处理（与参考代码完全一致）
    const handleWpChange = (event: SelectChangeEvent<string[]>) => {
        const value = event.target.value;
        setForm({ ...form, code: typeof value === 'string' ? value.split(',') : value });
    };

    const unescapeSymbol = (str: string) => {
        try {
            return JSON.parse(`"${str}"`);
        } catch {
            return str;
        }
    };

    return (
        <Dialog open={open} onClose={() => onClose(false)} fullWidth maxWidth="sm">
            <DialogTitle>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    {isEdit ? 'Edit Objective' : 'Create Objective'}
                    <IconButton size="small" onClick={() => onClose(false)}>
                        <CloseIcon />
                    </IconButton>
                </Stack>
            </DialogTitle>
            <DialogContent>
                {loading ? (
                    <Stack spacing={2} mt={1}>
                        {[1, 2, 3, 4].map(i => <Skeleton key={i} variant="rounded" height={40} />)}
                    </Stack>
                ) : (
                    <Stack spacing={2} mt={1}>
                        <TextField
                            size="small"
                            label="table level"
                            required
                            type="number"
                            inputProps={{ min: 0, max: 4, step: 1 }}
                            helperText={fieldErrors.parentId || '0 = Main table , 1~4 = Sub-table level'}
                            error={!!fieldErrors.parentId}
                            value={form.parentId}
                            onChange={(e) => setForm({ ...form, parentId: e.target.value })}
                        />            
                        <TextField
                            size="small"
                            label="Symbol"
                            value={unescapeSymbol(form.symbol)}
                            onChange={(e) => setForm({ ...form, symbol: e.target.value })}
                        />   
                        <TextField
                            size="small"
                            label="Content"
                            required
                            multiline
                            minRows={2}
                            error={!!fieldErrors.content}
                            helperText={fieldErrors.content}
                            value={form.content}
                            onChange={(e) => setForm({ ...form, content: e.target.value })}
                        />
                        {/* Working Paper 多选 - 完全参照参考代码 */}
                        <FormControl size="small" fullWidth>
                            <InputLabel id="wp-select-label">Working Paper</InputLabel>
                            <Select
                                labelId="wp-select-label"
                                multiple
                                value={form.code}
                                onChange={handleWpChange}
                                input={<OutlinedInput label="Working Paper" />}
                                renderValue={(selected) => (
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                        {selected.map((code) => (
                                            <Chip key={code} label={wpOptions[code] || code} size="small" />
                                        ))}
                                    </Box>
                                )}
                            >
                                {Object.entries(wpOptions).map(([code, name]) => (
                                    <MenuItem key={code} value={code}>
                                        <Checkbox size="small" checked={form.code.indexOf(code) > -1} />
                                        {name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    size="small"
                                    checked={form.isDel === 1}
                                    onChange={(e) => setForm({ ...form, isDel: e.target.checked ? 1 : 0 })}
                                />
                            }
                            label="Is Deleted"
                        />
                        {fieldErrors._global && (
                            <FormHelperText error>{fieldErrors._global}</FormHelperText>
                        )}
                    </Stack>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={() => onClose(false)}>Cancel</Button>
                <Button variant="contained" onClick={handleSave} disabled={saving || loading}>
                    {saving ? 'Saving...' : 'Save'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}